import React, { useState, useEffect } from 'react';
import type { Socket } from 'socket.io-client';

import { warningAPI } from '../../../utils/apiClient';
import logger from '../../../utils/logger';

/**
 * Warning object from backend
 */
interface MaintenanceWarning {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  type: string;
  message: string;
  deviceId?: string;
  acknowledged?: boolean;
  createdAt: Date;
}

/**
 * Props for the MaintenanceButton component
 */
export interface MaintenanceButtonProps {
  /** Function to call when button is clicked */
  onClick: () => void;
  /** Socket.io instance for real-time updates */
  socket?: Socket | null;
  /** Additional styles to apply */
  style?: React.CSSProperties;
}

/**
 * MaintenanceButton Component - Maintenance access button
 *
 * Always visible button for SuperAdmin users to access maintenance page.
 * Blinks when there are unacknowledged maintenance warnings.
 * Shows warning count and severity indicator when warnings exist.
 * Shows blue with wrench icon when no warnings.
 *
 * Uses hybrid socket + polling approach:
 * - Socket events for real-time updates when warnings change
 * - 2-minute polling as backup to catch missed events
 *
 * @example
 * <MaintenanceButton
 *   onClick={() => navigate('/maintenance')}
 *   socket={socketInstance}
 * />
 */
export const MaintenanceButton: React.FC<MaintenanceButtonProps> = ({
  onClick,
  socket,
  style = {},
}) => {
  const [blink, setBlink] = useState(false);
  const [warnings, setWarnings] = useState<MaintenanceWarning[]>([]);

  // Fetch warnings from backend
  useEffect(() => {
    fetchWarnings();

    // 2-minute polling as backup
    const interval = setInterval(fetchWarnings, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, []);

  // Listen for socket events for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleWarningUpdate = () => {
      logger.debug('Received warning-update event');
      fetchWarnings();
    };

    socket.on('warning-update', handleWarningUpdate);

    return () => {
      socket.off('warning-update', handleWarningUpdate);
    };
  }, [socket]);

  const fetchWarnings = async () => {
    try {
      const token = window.localStorage.getItem('token');
      if (!token) return;

      const data = await warningAPI.getWarnings({ status: 'active', device_status: 'active' });

      if (data.success && data.warnings) {
        setWarnings(data.warnings);
      }
    } catch (err) {
      logger.error('Error fetching warnings for maintenance button:', err);
    }
  };

  // Get warning counts from backend warnings
  const unacknowledgedCount = warnings.length;
  const criticalCount = warnings.filter((w) => w.severity === 'critical').length;
  const hasCritical = criticalCount > 0;

  // Blink effect when there are unacknowledged warnings
  useEffect(() => {
    if (unacknowledgedCount > 0) {
      const interval = setInterval(() => {
        setBlink((prev) => !prev);
      }, 1000); // Blink every second

      return () => clearInterval(interval);
    }
    setBlink(false);
    return undefined;
  }, [unacknowledgedCount]);

  const baseStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '2vmin',
    left: '2vmin',
    zIndex: 2100,
    padding: '1vmin 2vmin',
    fontSize: '2vmin',
    fontWeight: 'bold',
    borderRadius: '1vmin',
    border: '2px solid',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '1vmin',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    ...style,
  };

  // Color based on severity and blink state
  const backgroundColor =
    unacknowledgedCount === 0
      ? '#2196F3' // Blue when no warnings
      : blink
        ? hasCritical
          ? '#ff5252'
          : '#ff9800'
        : hasCritical
          ? '#f44336'
          : '#ff9800';

  const textColor = 'white';
  const borderColor =
    unacknowledgedCount === 0
      ? '#1976D2' // Darker blue when no warnings
      : hasCritical
        ? '#d32f2f'
        : '#f57c00';

  return (
    <div
      style={{
        ...baseStyle,
        background: backgroundColor,
        color: textColor,
        borderColor: borderColor,
      }}
      onClick={onClick}
    >
      {/* Warning icon */}
      <span style={{ fontSize: '2.5vmin' }}>
        {unacknowledgedCount === 0 ? '🔧' : hasCritical ? '🚨' : '⚠️'}
      </span>

      {/* Warning text and count */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '1.8vmin', fontWeight: 'bold' }}>Maintenance</div>
        {unacknowledgedCount > 0 ? (
          <div style={{ fontSize: '1.4vmin' }}>
            {unacknowledgedCount} {unacknowledgedCount === 1 ? 'warning' : 'warnings'}
            {hasCritical && ` (${criticalCount} critical)`}
          </div>
        ) : (
          <div style={{ fontSize: '1.4vmin' }}>Click for maintenance</div>
        )}
      </div>

      {/* Pulse animation for critical warnings */}
      {hasCritical && (
        <style>
          {`
            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
          `}
        </style>
      )}
    </div>
  );
};

export default MaintenanceButton;
